// Pure shipping-method classifiers — by STABLE METHOD IDENTITY, not label text.
// Mirrors the PHP source of truth (inc/ship-classify.php): BoxNow ships as method_id
// `box_now_delivery` (+ the `disabled_boxnow` over-weight placeholder); OUR two
// collection points are a fixed set of Flexible-Shipping instance IDs, handed to the
// bundle via window.{CHECKOUT_NEXT,CART_NEXT}.shipIds. Everything else — including the
// courier pickup-point tiers whose labels also read «Παραλαβή απο το κατάστημα» — is a
// courier. No Greek label matching, so a method rename can never misfile a rate.
//
// Extracted so it can be unit-tested without React/window: every classifier takes the
// identity sets as an explicit argument, falling back to the localized config.

const BOXNOW_METHODS = ['box_now_delivery', 'disabled_boxnow'];

// Localized identity sets: { pickup:[int], locker:[int], boxnow:[string] }.
export function shipIds() {
  const w = (typeof window !== 'undefined' && (window.CHECKOUT_NEXT || window.CART_NEXT)) || {};
  return w.shipIds || {};
}

// Split "method_id:instance_id" → [methodId, instanceId|null].
export function rateIdentity(id) {
  const s = String(id || '');
  const i = s.indexOf(':');
  if (i === -1) return [s, null];
  const inst = parseInt(s.slice(i + 1), 10);
  return [s.slice(0, i), Number.isNaN(inst) ? null : inst];
}

// Box Now (real rate or the over-weight disabled placeholder). Id-based.
export function isBoxnow(id) {
  const [mid] = rateIdentity(id);
  return BOXNOW_METHODS.indexOf(mid) !== -1;
}

// OUR store pickup: core local_pickup, or one of our pickup instance IDs.
export function isPickup(id, pickupInstances) {
  const [mid, inst] = rateIdentity(id);
  if (mid === 'local_pickup') return true;
  const set = pickupInstances || shipIds().pickup || [];
  return inst !== null && set.indexOf(inst) !== -1;
}

// Is OUR pickup a self-service locker (vs the staffed counter)? Icon only.
export function isLockerPickup(id, lockerInstances) {
  const [, inst] = rateIdentity(id);
  const set = lockerInstances || shipIds().locker || [];
  return inst !== null && set.indexOf(inst) !== -1;
}

// Classify a method into the cart's shipping groups (pickup | boxnow | courier).
export function classifyGroup(id, pickupInstances) {
  if (isPickup(id, pickupInstances)) return 'pickup';
  if (isBoxnow(id)) return 'boxnow';
  return 'courier';
}

// Which rate rows the checkout shows outright, and which stay folded behind the
// «περισσότερες επιλογές» expander.
//
// The visible set follows the group of the CURRENTLY SELECTED method, and every
// other group stays in the fold — never dropped. Dropping them made group changes
// one-way: a shopper who reached store pickup by a stray tap in the cart's mobile
// sheet had no delivery row to switch to at all (order 1617618), and once that was
// fixed, switching to a courier made the pickup rows disappear in turn, so the move
// could not be undone. Nothing is unreachable from anywhere now.
//
// The fold's ORDER still follows the group in view: whoever is arranging a delivery
// sees the other delivery options first and collection last.
//
// Within collection, the staffed counter is listed above the 24/7 smart locker
// (owner 2026-10-20) — the zone hands them over the other way round.
//
// Returns { primary, others, group }; `others` is deduped against `primary`, so the
// "no rates in this group" fallback row can never render twice.
export function splitMethodGroups(methods, selectedId, pickupInstances, lockerInstances) {
  const list = Array.isArray(methods) ? methods : [];
  const g = (m) => classifyGroup(m.id, pickupInstances);
  const selected = list.find((m) => m.id === selectedId);
  const group = selected ? g(selected) : 'courier';
  const couriers = list.filter((m) => g(m) === 'courier');
  const boxnow = list.filter((m) => g(m) === 'boxnow');
  const pickupAll = list.filter((m) => g(m) === 'pickup');
  const isLocker = (m) => isLockerPickup(m.id, lockerInstances);
  const pickup = pickupAll.filter((m) => !isLocker(m)).concat(pickupAll.filter(isLocker));

  let primary;
  let fold;
  if (group === 'pickup') {
    primary = pickup;
    fold = couriers.concat(boxnow);
  } else if (group === 'boxnow') {
    primary = boxnow.length ? boxnow : list.slice(0, 1);
    fold = couriers.concat(pickup);
  } else {
    primary = couriers.length ? couriers : list.slice(0, 1);
    fold = boxnow.concat(pickup);
  }

  const shown = {};
  primary.forEach((m) => { shown[m.id] = true; });
  let others = fold.filter((m) => !shown[m.id]);
  // The selected method is always visible, whichever list it landed in.
  if (!primary.some((m) => m.id === selectedId)) {
    const sel = others.find((m) => m.id === selectedId);
    if (sel) {
      primary = primary.concat([sel]);
      others = others.filter((m) => m.id !== selectedId);
    }
  }
  return { primary, others, group };
}
